import { useState, useRef, useEffect } from "react";
import api from '../config/api';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { DOCUMENT_COUNTRIES } from "../portal/documentCountries";
import LoadingButton from "@mui/lab/LoadingButton";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import { useTranslation } from 'react-i18next';
import LogoUploader from "../layouts/LogoUploader";
import { POINTS_SERVICES } from "./pointsServices";
import useStore from "../config/store";

const OrganizationModal = ({
  organization,
  openModal,
  handleClose,
  type,
  setCounter,
  showAlert,
  authToken,
}) => {
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(organization?.imgPath || null);
  // Sistema de puntos opcional al crear (solo modo "add")
  const [pointsEnabled, setPointsEnabled] = useState(false);
  const [pointsToLoad, setPointsToLoad] = useState("");
  // Puntos ilimitados al crear (2026-10-29): manda pointsLimit -1
  const [pointsUnlimited, setPointsUnlimited] = useState(false);
  const [selectedServices, setSelectedServices] = useState([...POINTS_SERVICES]);
  // Logo opcional al crear: se elige localmente y se sube después de crear la org
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  const { t } = useTranslation();
  const { user } = useStore.getState();
  const isSuperAdmin = user?.rol === 'super-admin';
  // Módulo ERP (solo super-admin, modo edit): switch que setea el campo
  // erpEnabled de la organización vía PUT /erp/settings
  const [erpEnabled, setErpEnabled] = useState(false);
  const [erpInitial, setErpInitial] = useState(false);
  // Módulos ATS y Hunting (solo super-admin, modo edit, 2026-10-26): campos
  // atsEnabled / huntingEnabled vía GET/PUT /organizations/modules. Los
  // switches quedan deshabilitados hasta leer el valor guardado.
  const [modules, setModules] = useState({ ats: false, hunting: false });
  const [modulesInitial, setModulesInitial] = useState({ ats: false, hunting: false });
  const [modulesLoaded, setModulesLoaded] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      if (!openModal || type !== 'edit' || !isSuperAdmin || !organization?.uid) return;
      setModulesLoaded(false);
      try {
        const response = await api.get('/organizations/modules', {
          params: { organizationId: organization.uid },
          headers: { authorization: `Bearer ${authToken}` },
        });
        const data = response.data.response?.data || {};
        const loaded = { ats: data.ats === true, hunting: data.hunting === true };
        setModules(loaded);
        setModulesInitial(loaded);
        setModulesLoaded(true);
      } catch (err) {
        console.error(err);
      }
    };
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, type, organization?.uid]);

  useEffect(() => {
    const fetchErpEnabled = async () => {
      if (!openModal || type !== 'edit' || !isSuperAdmin || !organization?.uid) return;
      try {
        const response = await api.get('/erp/enabled', {
          params: { organizationId: organization.uid },
          headers: { authorization: `Bearer ${authToken}` },
        });
        const enabled = response.data.response?.data?.enabled === true;
        setErpEnabled(enabled);
        setErpInitial(enabled);
      } catch (err) {
        console.error(err);
      }
    };
    fetchErpEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, type, organization?.uid]);

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      showAlert(t("branding.invalidImage"), "error");
      return;
    }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogoForNewOrg = async (newOrgUid) => {
    if (!logoFile || !newOrgUid) return;
    try {
      const fd = new FormData();
      fd.append("logo", logoFile);
      await api.post(
        `/organizations/${newOrgUid}/logo`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } catch (err) {
      // La organización ya fue creada; solo avisar que falló el logo
      console.error(err);
      showAlert(t("branding.uploadError"), "warning");
    }
  };

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (type !== "delete") {
      const formEl = e.target.closest("form");
      const formData = new FormData(formEl);
      const entries = {};
      for (let [key, value] of formData.entries()) entries[key] = value.trim();
      if (type === "add") {
        try {
          // Sistema de puntos opcional: si el switch está apagado,
          // el POST es idéntico al flujo actual
          const payload = pointsEnabled
            ? {
                ...entries,
                enablePointsSystem: true,
                pointsLimit: pointsUnlimited ? -1 : Number(pointsToLoad) || 0,
                availableServices: selectedServices,
              }
            : entries;

          const response = await api.post(
            `/organizations`,
            payload,
            { headers: { authorization: `Bearer ${authToken}` } }
          );

          if (response.data.response.status_response === "OK") {
            // Subir el logo (si se eligió) usando el uid de la org recién creada
            await uploadLogoForNewOrg(response.data.response.data?.uid);

            showAlert(t('organizations.organizationCreateSuccess'), "info");
            setCounter((prev) => {
              return prev + 1;
            });
            setLoading(false);
            closeModal();
          } else {
            setLoading(false);
            showAlert(t('organizations.organizationCreateError'), "warning");
          }
        } catch (err) {
          console.error(err);
          setLoading(false);
          showAlert(t('organizations.organizationCreateError'), "warning");
        }
      } else {
        try {
          let response = await api.put(
            `/organizations/${organization.uid}`,
            entries,
            {
              headers: { authorization: `${authToken}` },
            }
          );
          if (response.data.response.status_response === "OK") {
            // Si falla un guardado aparte (ERP o módulos), se muestra ese aviso
            // en vez del de éxito (antes el de éxito lo tapaba)
            let partialError = null;
            // Habilitación del módulo ERP: endpoint propio (super-admin),
            // separado del PUT de organización (whitelist de campos)
            if (isSuperAdmin && erpEnabled !== erpInitial) {
              try {
                await api.put(
                  '/erp/settings',
                  { organizationId: organization.uid, enabled: erpEnabled },
                  { headers: { authorization: `Bearer ${authToken}` } }
                );
              } catch (erpErr) {
                console.error(erpErr);
                partialError = t('erp.admin.saveError');
              }
            }
            // Módulos ATS y Hunting (super-admin): solo lo que cambió
            const modulesChange = {};
            if (modules.ats !== modulesInitial.ats) modulesChange.ats = modules.ats;
            if (modules.hunting !== modulesInitial.hunting) modulesChange.hunting = modules.hunting;
            if (isSuperAdmin && modulesLoaded && Object.keys(modulesChange).length > 0) {
              try {
                const modulesRes = await api.put(
                  '/organizations/modules',
                  { organizationId: organization.uid, ...modulesChange },
                  { headers: { authorization: `Bearer ${authToken}` } }
                );
                // Si es la organización en la que se está trabajando, el
                // toggle de modos lo refleja sin volver a elegirla
                const saved = modulesRes.data?.response?.data;
                const store = useStore.getState();
                if (saved && store.organization === organization.uid) {
                  store.setOrganizationModules({ ats: saved.ats === true, hunting: saved.hunting === true });
                }
              } catch (modulesErr) {
                console.error(modulesErr);
                partialError = t('organizations.modules.saveError');
              }
            }
            setCounter((prev) => {
              return prev + 1;
            });
            if (partialError) {
              showAlert(partialError, "warning");
            } else {
              showAlert(t('organizations.organizationEditSuccess'), "info");
            }
            setLoading(false);
            closeModal();
          } else {
            showAlert(t('organizations.organizationEditError'), "warning");
            setLoading(false);
          }
        } catch (err) {
          console.error(err);
          showAlert(t('organizations.organizationEditError'), "warning");
          setLoading(false);
        }
      }
    } else {
      try {
        let response = await api.delete(
          `/organizations/${organization.uid}`,
          {
            headers: { authorization: `${authToken}` },
          }
        );
        if (response.data.response.status_response === "OK") {
          showAlert(t('organizations.organizationDeleteSuccess'), "info");
          setLoading(false);
          setCounter((prev) => {
            return prev + 1;
          });
          closeModal();
        } else {
          showAlert(t('organizations.organizationDeleteError'), "warning");
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        showAlert(t('organizations.organizationDeleteError'), "warning");
        setLoading(false);
      }
    }
  };

  const closeModal = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    handleClose();
  };

  return (
    <Modal
      disableScrollLock={true}
      open={openModal}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 540,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          paddingBottom: 2,
        }}
      >
        <Container maxWidth="xl">
          {type !== "delete" ? (
            <Box>
              <Grid
                component="form"
                onSubmit={handleSubmit}
                container
                spacing={2}
              >
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    component="h5"
                    sx={{ textAlign: "center" }}
                  >
                    {type === "add"
                      ? t('organizations.organizationNew')
                      : t('organizations.organizationEdit')}
                  </Typography>
                </Grid>

                {type === "add" && (
                  <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
                    <Box sx={{ position: "relative", width: 84, height: 84 }}>
                      <Avatar
                        src={logoPreview || undefined}
                        variant="rounded"
                        sx={{ width: 84, height: 84, bgcolor: "#EDE9FE", color: "#6A5AF9" }}
                      >
                        {!logoPreview && <PhotoCameraIcon />}
                      </Avatar>
                      <Tooltip title={t("branding.changeLogo")}>
                        <IconButton
                          size="small"
                          onClick={() => logoInputRef.current?.click()}
                          sx={{
                            position: "absolute", bottom: -6, right: -6,
                            width: 26, height: 26,
                            backgroundColor: "#6A5AF9", color: "#fff",
                            "&:hover": { backgroundColor: "#5B4DE8" },
                          }}
                        >
                          <PhotoCameraIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleLogoPick}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {t('branding.orgLogo')}
                    </Typography>
                  </Grid>
                )}

                {type === "edit" && (
                  <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
                    <LogoUploader
                      currentUrl={logoUrl}
                      uploadUrl={`/organizations/${organization?.uid}/logo`}
                      onUploaded={(url) => { setLogoUrl(url); if (setCounter) setCounter((p) => p + 1); }}
                      size={84}
                      shape="square"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {t('branding.orgLogo')}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControl required fullWidth sx={{ mt: 2 }}>
                    <TextField
                      name="name"
                      label={t('organizations.organizationName')}
                      defaultValue={organization?.name}
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </FormControl>
                </Grid>

                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <TextField
                        select
                        name="country"
                        label={t('organizations.organizationCountry')}
                        defaultValue={organization?.country || ''}
                        variant="outlined"
                        fullWidth
                      >
                        {DOCUMENT_COUNTRIES.filter((c) => c.name).map((c) => (
                          <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  </Grid>
                )}

                {type === "edit" && isSuperAdmin && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={modules.ats}
                          disabled={!modulesLoaded}
                          onChange={(e) => setModules((prev) => ({ ...prev, ats: e.target.checked }))}
                        />
                      }
                      label={t('organizations.modules.ats')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t('organizations.modules.atsHelper')}
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={modules.hunting}
                          disabled={!modulesLoaded}
                          onChange={(e) => setModules((prev) => ({ ...prev, hunting: e.target.checked }))}
                        />
                      }
                      label={t('organizations.modules.hunting')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t('organizations.modules.huntingHelper')}
                    </Typography>
                  </Grid>
                )}

                {type === "edit" && isSuperAdmin && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={erpEnabled}
                          onChange={(e) => setErpEnabled(e.target.checked)}
                        />
                      }
                      label={t('erp.admin.moduleSwitch')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t('erp.admin.moduleSwitchHelper')}
                    </Typography>
                  </Grid>
                )}

                {type === "add" && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={pointsEnabled}
                          onChange={(e) => setPointsEnabled(e.target.checked)}
                        />
                      }
                      label={t('organizations.points.enableSwitch')}
                    />

                    {pointsEnabled && (
                      <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={pointsUnlimited}
                              onChange={(e) => setPointsUnlimited(e.target.checked)}
                            />
                          }
                          label={t('organizations.points.unlimitedSwitch')}
                        />
                        <TextField
                          label={t('organizations.points.pointsToLoad')}
                          type="number"
                          value={pointsToLoad}
                          onChange={(e) => setPointsToLoad(e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                          size="small"
                          sx={{ mb: 1 }}
                          disabled={pointsUnlimited}
                          helperText={pointsUnlimited ? t('organizations.points.unlimitedHelper') : ''}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                          {t('organizations.points.modulesTitle')}
                        </Typography>
                        <FormGroup sx={{ maxHeight: 180, overflowY: "auto" }}>
                          {POINTS_SERVICES.map((service) => (
                            <FormControlLabel
                              key={service}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedServices.includes(service)}
                                  onChange={() => toggleService(service)}
                                />
                              }
                              label={t(`organizations.points.services.${service}`)}
                            />
                          ))}
                        </FormGroup>
                        {pointsUnlimited && selectedServices.includes('hunting') && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            {t('organizations.points.huntingUnlimitedWarning')}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                >
                  <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={handleSubmit}
                    loadingIndicator={t('saving')}
                    sx={[
                      {
                        alignSelf: "flex-end",
                        mt: 0.2,
                        mb: 0,
                        boxShadow: "none",
                        width: "75%",
                      },
                    ]}
                  >
                    {type === "add" ? t('create') : t('save')}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid container columnSpacing={2} rowSpacing={4}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: "justify" }}>
                  {t('organizations.organizationDeleteConfirm')}{" "}
                  <strong>{organization?.name}</strong>?
                </Typography>
              </Grid>

              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <LoadingButton
                  variant="contained"
                  color="error"
                  sx={[
                    {
                      alignSelf: "flex-end",
                      mt: 0.2,
                      mb: 0,
                      boxShadow: "none",
                      width: "75%",
                    },
                    (_theme) => ({
                      "&:hover": {
                        backgroundColor: "#b42f2f",
                      },
                    }),
                  ]}
                  loading={loading}
                  loadingIndicator="Eliminando..."
                  onClick={handleSubmit}
                >
                  {t('yes')}
                </LoadingButton>
              </Grid>
              <Grid item xs={6}>
                <Button
                  color="inherit"
                  variant="outlined"
                  sx={{
                    alignSelf: "flex-end",
                    mt: 0.2,
                    mb: 0,
                    boxShadow: "none",
                    width: "75%",
                  }}
                  onClick={closeModal}
                >
                  {t('no')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </Modal>
  );
};

export default OrganizationModal;
